import { RetailerStats } from "api/admin";

/**
 * The three parts of the clocks we sent to a retailer. They always add up to
 * "clocks sent":
 * - sold: marked sold (registered, or online weeks after the order)
 * - notSold: has a clock id, not found sold (yet)
 * - withoutId: no clock id on the order yet, so it cannot be checked
 */
export type ClockSegments = {
  sold: number;
  notSold: number;
  withoutId: number;
};

type ClockCounts = Pick<
  RetailerStats,
  "clocksSent" | "clocksSold" | "clocksWithoutId"
>;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const round1 = (value: number) => Math.round(value * 10) / 10;

/**
 * Splits the counts so the parts always add up to the clocks sent, even if the
 * counts disagree a little (e.g. an order edited after the clocks were counted).
 */
export const clockSegments = ({
  clocksSent,
  clocksSold,
  clocksWithoutId,
}: ClockCounts): ClockSegments => {
  const sent = Math.max(0, clocksSent);
  const sold = clamp(clocksSold, 0, sent);
  const withoutId = clamp(clocksWithoutId, 0, sent - sold);
  return { sold, withoutId, notSold: sent - sold - withoutId };
};

/** Percent of the clocks sent that are sold, null when nothing was sent. */
export const soldPercent = (sold: number, sent: number): number | null =>
  sent > 0 ? round1((sold / sent) * 100) : null;

export const formatPercent = (value: number | null): string =>
  value === null ? "—" : `${value}%`;

export const formatDays = (value: number | null): string =>
  value === null ? "—" : `${value} ${value === 1 ? "day" : "days"}`;

export const pluralize = (count: number, word: string): string =>
  `${count} ${word}${count === 1 ? "" : "s"}`;

export type RetailerChartRow = ClockSegments & {
  id: string;
  name: string;
  orders: number;
  clocksSent: number;
  soldPct: number | null;
  /** The second line under the name: "5 orders · 27% sold". */
  subtitle: string;
};

/** Names compare without regard to case, like the API does. */
const compareNames = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

/** Most clocks first; ties by name. */
const bySentThenName = (a: RetailerStats, b: RetailerStats) =>
  b.clocksSent - a.clocksSent || compareNames(a, b);

/**
 * One row per retailer for the stacked chart. A retailer with no orders in the
 * period has nothing to draw, so it is left out (it stays in the dropdown).
 */
export const buildChartRows = (stats: RetailerStats[]): RetailerChartRow[] =>
  [...stats]
    .filter((retailer) => retailer.orders > 0)
    .sort(bySentThenName)
    .map((retailer) => {
      const segments = clockSegments(retailer);
      const soldPct = soldPercent(segments.sold, retailer.clocksSent);
      return {
        ...segments,
        id: retailer.id,
        name: retailer.name,
        orders: retailer.orders,
        clocksSent: retailer.clocksSent,
        soldPct,
        subtitle: `${pluralize(retailer.orders, "order")} · ${formatPercent(soldPct)} sold`,
      };
    });

/** Clocks of the retailers drawn that cannot be checked yet, for the note under the chart. */
export const totalWithoutId = (rows: RetailerChartRow[]): number =>
  rows.reduce((sum, row) => sum + row.withoutId, 0);

export const STATS_CSV_HEADERS = [
  "Retailer",
  "Orders",
  "Clocks sent",
  "Sold",
  "Not sold yet",
  "No clock id yet",
  "Sold %",
];

/** Every retailer, including those with no orders in the period (the CSV is the full picture). */
export const buildStatsCsvRows = (
  stats: RetailerStats[],
): (string | number)[][] =>
  [...stats].sort(bySentThenName).map((retailer) => {
    const segments = clockSegments(retailer);
    const soldPct = soldPercent(segments.sold, retailer.clocksSent);
    return [
      retailer.name,
      retailer.orders,
      retailer.clocksSent,
      segments.sold,
      segments.notSold,
      segments.withoutId,
      soldPct === null ? "" : soldPct,
    ];
  });

/** Shortens a long company name for the axis, the full name stays in the tooltip. */
export const truncate = (text: string, max: number): string =>
  text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
